import { useEffect, useState } from 'react';
import { getMonthlySeries } from '../services';
import { IMonthlySeriesItem, HookState } from '../types';
import { resolveErrorMessage } from './hookUtils';

// Cache de módulo para endpoints pesados (1 fetch por sesión salvo refresh explícito)
const moduleCache: Map<string, { data: IMonthlySeriesItem[]; cachedAt: string; fetchedAt: number }> = new Map();
const STALE_MS = 15 * 60 * 1000;

function getCacheKey(sellerId?: number, team?: string): string {
	return `${sellerId ?? ''}-${team ?? ''}`;
}

export function useMonthlySeries(options?: { sellerId?: number; team?: string }): HookState<IMonthlySeriesItem[]> {
	const [state, setState] = useState<HookState<IMonthlySeriesItem[]>>({
		data: null,
		loading: true,
		error: null,
		cachedAt: null,
	});

	const sellerId = options?.sellerId;
	const team = options?.team;

	useEffect(() => {
		let cancelled = false;
		const cacheKey = getCacheKey(sellerId, team);
		const cached = moduleCache.get(cacheKey);

		if (cached && Date.now() - cached.fetchedAt < STALE_MS) {
			setState({ data: cached.data, loading: false, error: null, cachedAt: cached.cachedAt });
			return;
		}

		setState((prev) => ({ ...prev, loading: true, error: null }));

		getMonthlySeries({ sellerId, team })
			.then(({ data, cachedAt }) => {
				if (cancelled) return;
				moduleCache.set(cacheKey, { data, cachedAt: cachedAt ?? '', fetchedAt: Date.now() });
				setState({ data, loading: false, error: null, cachedAt });
			})
			.catch((err) => {
				if (!cancelled) setState({ data: null, loading: false, error: resolveErrorMessage(err), cachedAt: null });
			});

		return () => {
			cancelled = true;
		};
	}, [sellerId, team]);

	return state;
}
